import i18next from 'i18next'
import React, { ReactNode, useState } from 'react'
import fiLocale from 'antd/lib/locale/fi_FI'
import enLocale from 'antd/lib/locale/en_US'
import svLocale from 'antd/lib/locale/sv_SE'
import { ConfigProvider, Spin, theme } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { Locale } from 'antd/es/locale'
import { sessionLayoutSelector } from '../redux/session/contract/selectors'
import { Language } from '../types/user/User'
import { customerThemes } from './customerThemes'
import { TABLE_FONT_SIZE, TABLE_HEADER_BACKGROUND, TABLE_HEADER_TEXT_COLOR } from './tableTokens'

const getLocale = (lang: Language) => {
  switch (lang) {
    case Language.en:
      return enLocale
    case Language.sv:
      return svLocale
    case Language.fi:
    default:
      return fiLocale
  }
}

Spin.setDefaultIndicator(<LoadingOutlined />)

interface AppConfigProviderProps {
  children?: ReactNode
}

const { compactAlgorithm } = theme

const AppConfigProvider: React.FC<AppConfigProviderProps> = ({ children }) => {
  const [locale, setLocale] = useState<Locale>(getLocale(i18next.language as Language))
  const whiteLabel = useSelector(sessionLayoutSelector)
  const token = customerThemes[whiteLabel]?.token || customerThemes.app.token

  i18next.on('languageChanged', (lng: Language) => setLocale(getLocale(lng)))

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: 'Manrope',
          fontSize: 14,
          // Dividing borders (cards, table rows, dividers) default to a faint #f0f0f0 that barely shows on
          // the white content panel; use a slightly stronger grey instead.
          colorBorderSecondary: '#e5e5e5',
          ...token
        },
        algorithm: [compactAlgorithm],
        components: {
          Dropdown: {
            zIndexPopup: 11000
          },
          Table: {
            lineHeight: 1.2,
            // Match ag-grid (config/agGrid/theme)
            cellFontSizeSM: TABLE_FONT_SIZE,
            headerBg: TABLE_HEADER_BACKGROUND,
            headerColor: TABLE_HEADER_TEXT_COLOR
          },
          Badge: {
            fontSizeSM: 20
          }
        }
      }}
      modal={{
        closable: true,
        mask: { closable: false }
      }}
      locale={locale}
    >
      {children}
    </ConfigProvider>
  )
}

export default AppConfigProvider
